import {
  planWorkerQueryParamChunks,
  type WorkerQueryChunkPlan,
} from "#worker-query-param-chunking";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { d1Codecs } from "@-/com.npmjs.drizzle-orm/d1";
import type { SqliteProxyExecutors } from "@-/com.npmjs.drizzle-orm/sqlite-proxy";

/**
 * Codecs for a `sqlite-proxy` database backed by the worker. The statements run on D1 and their
 * rows arrive as JSON, so they decode with D1's codecs. Since drizzle-orm 1.0.0-rc.5 a dialect
 * without codecs leaves integer booleans, JSON text and timestamps undecoded and no longer `hex()`s
 * blobs inside relational JSON, which D1 then rejects.
 */
export const workerQueryCodecs = d1Codecs;

/**
 * How the worker returns rows: positional arrays (`array`, the default and what every query
 * builder and relational query asks for) or column-keyed objects (`object`, which drizzle only
 * asks for on raw `db.all(sql)` / `db.get(sql)`).
 */
export type WorkerQueryRowMode = "array" | "object";

/**
 * Sends one `/query` POST to the worker and returns its `rows` value verbatim.
 *
 * Implementations own transport concerns (URL/selector, retries, error shaping) and MUST return
 * `rows` unchanged — in particular they must not coerce the `get` no-match `null` into `[]`.
 * `rowMode` is omitted for array rows, so those requests stay identical to the pre-rc.5 shape.
 */
export type WorkerQuerySend = (args: {
  method: string;
  params: readonly unknown[];
  rowMode?: WorkerQueryRowMode;
  sql: string;
}) => Promise<unknown>;

/**
 * Run a proxied query, transparently splitting it into several under-cap statements when it would
 * otherwise exceed D1's 100-bound-parameter limit.
 *
 * This is the single place the limit is handled, so every remote database read gets it for free —
 * call sites keep issuing one natural set-based query (`where: { glyph: { in: allGraphemes } }`)
 * regardless of set size. When the statement is small enough, or its shape makes splitting unsound,
 * it is sent unchanged (see {@link planWorkerQueryParamChunks}).
 *
 * Chunks run with BOUNDED concurrency rather than strictly sequentially. Sequential was safe but
 * pathological once `online` became the default static-data mode: an oversized read (HSK 7 browse
 * fans out to ~52 statements) turned into ~52 serial round-trips, seconds of latency on one screen.
 * Unbounded parallelism is not the fix either — a same-tick burst of dozens of POSTs stalls the
 * browser's per-origin connection pool and competes with the rest of the screen's queries, which is
 * what the original sequential comment was guarding against. A small window keeps the pool healthy
 * while collapsing the latency.
 *
 * Result ORDER is preserved (results are written back positionally, not appended on completion) —
 * callers rely on chunking being invisible, and `planWorkerQueryParamChunks` only splits statements
 * whose row order is not otherwise constrained.
 */
const WORKER_QUERY_CHUNK_CONCURRENCY = 6;

export async function executeWorkerQuery({
  method,
  params,
  rowMode,
  send,
  sql,
}: {
  method: string;
  params: readonly unknown[];
  rowMode?: WorkerQueryRowMode;
  send: WorkerQuerySend;
  sql: string;
}): Promise<unknown> {
  const plan: WorkerQueryChunkPlan | null = planWorkerQueryParamChunks(sql, params, method);
  if (plan === null) {
    return send({ method, params, rowMode, sql });
  }

  const { chunks } = plan;
  const chunkResults: unknown[][] = Array.from({ length: arrayLength(chunks) }, () => []);
  let nextIndex = 0;

  async function runWorker(): Promise<void> {
    for (;;) {
      const index = nextIndex;
      nextIndex += 1;
      const chunk = chunks[index];
      if (chunk === undefined) {
        return;
      }
      const chunkRows = await send({ method, params: chunk.params, rowMode, sql: chunk.sql });
      if (Array.isArray(chunkRows)) {
        chunkResults[index] = chunkRows;
      }
    }
  }

  await Promise.all(
    Array.from(
      { length: Math.min(WORKER_QUERY_CHUNK_CONCURRENCY, arrayLength(chunks)) },
      runWorker,
    ),
  );

  return chunkResults.flat();
}

/**
 * drizzle `sqlite-proxy` executors that run every statement through {@link executeWorkerQuery}.
 *
 * Since drizzle-orm 1.0.0-rc.5 the proxy takes one executor per method instead of a single
 * `(sql, params, method) => { rows }` callback, and expects the rows back unwrapped: a row list for
 * `all`, one row or `undefined` for `get`. The worker answers a no-match `get` with `null` (JSON
 * has no `undefined`), which is mapped back here.
 */
export function createWorkerQueryExecutors(send: WorkerQuerySend): SqliteProxyExecutors {
  const all = async (
    sql: string,
    params: unknown[],
    rowMode?: WorkerQueryRowMode,
  ): Promise<unknown[]> => {
    const rows = await executeWorkerQuery({ method: "all", params, rowMode, send, sql });
    if (!Array.isArray(rows)) {
      throw new TypeError("Worker query `all` did not return a row list");
    }
    return rows;
  };
  const get = async (
    sql: string,
    params: unknown[],
    rowMode?: WorkerQueryRowMode,
  ): Promise<unknown> =>
    (await executeWorkerQuery({ method: "get", params, rowMode, send, sql })) ?? undefined;
  return {
    // The executor types are overloads keyed on `rowMode`, which one implementation that returns
    // whatever row shape the worker sent cannot express.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    all: all as SqliteProxyExecutors["all"],
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    get: get as SqliteProxyExecutors["get"],
    run: (sql, params) => executeWorkerQuery({ method: "run", params, send, sql }),
  };
}
